import {
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type SVGProps,
} from "react";
import type {
  Icon as PhosphorIcon,
  IconWeight,
} from "@phosphor-icons/react/dist/lib/types";

export type AppIconProps = Omit<SVGProps<SVGSVGElement>, "ref"> & {
  size?: number | string;
  weight?: IconWeight;
  mirrored?: boolean;
  alt?: string;
  /** Accepted for existing callers; Phosphor uses discrete weights. */
  absoluteStrokeWidth?: boolean;
};

export type AppIcon = ForwardRefExoticComponent<
  AppIconProps & RefAttributes<SVGSVGElement>
>;

/** Server-safe Phosphor renderer. Controls are regular unless a slot requests duotone. */
export function createAppIcon(
  Glyph: PhosphorIcon,
  name: string,
  defaultWeight: IconWeight = "regular",
): AppIcon {
  const Icon = forwardRef<SVGSVGElement, AppIconProps>(function AppIcon(
    {
      size = 24,
      weight = defaultWeight,
      strokeWidth: _strokeWidth,
      absoluteStrokeWidth: _absoluteStrokeWidth,
      stroke: _stroke,
      fill,
      className,
      alt,
      ...props
    },
    ref,
  ) {
    // Preserve selected favorite/rating states without applying SVG strokes to
    // Phosphor's filled outline paths. Existing spinners keep their CSS animation.
    const isSelected =
      ["Heart", "Star", "BookmarkSimple"].includes(name) &&
      ((fill != null && fill !== "none" && fill !== "transparent") ||
        /(?:^|\s)fill-(?!none|transparent)\S+/.test(className ?? ""));
    const labelled = Boolean(
      alt ||
        props["aria-label"] ||
        props["aria-labelledby"] ||
        props.role === "img",
    );
    return (
      <Glyph
        ref={ref}
        size={size}
        weight={isSelected && weight === "regular" ? "fill" : weight}
        alt={alt}
        className={className}
        aria-hidden={labelled ? undefined : true}
        focusable="false"
        data-icon-family="phosphor"
        {...props}
      />
    );
  });
  Icon.displayName = `${name}Icon`;
  return Icon;
}

// Compatibility types for existing feature contracts; all renderers are Phosphor.
export type LucideIcon = AppIcon;
export type LucideProps = AppIconProps;
